<template>
  <el-card class="box-card role full-screen">
    <div ref="pageHead" class="table-page-head">
      <el-form :inline="true" :model="queryForm">
        <el-form-item :label="$t('idNumber')">
          <el-input v-model="queryForm.idPiece" size="small"></el-input>
        </el-form-item>
        <el-form-item :label="$t('status')">
          <el-select
            v-model="queryForm.status"
            :placeholder="$t('all')"
            size="small"
          >
            <el-option :label="$t('all')" value=""></el-option>
            <el-option :label="$t('normal')" value="0"></el-option>
            <el-option :label="$t('wanted')" value="1"></el-option>
            <el-option :label="$t('frozen_account')" value="2"></el-option>
            <el-option :label="$t('PPE')" value="3"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('country')">
          <el-select
            size="medium"
            v-model="queryForm.country"
            filterable
            :placeholder="$t('Please select country')"
            style="width: 100%;"
          >
            <el-option
              v-for="item in countryList"
              :key="item.id"
              :label="$i18n.locale === 'fr' ? item.nameFr : item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item>
          <el-button
            type="primary"
            size="small"
            @click="getList"
            :loading="isloading"
            ><i class="el-icon-search"></i> {{ $t("query") }}</el-button
          >
        </el-form-item>
      </el-form>

      <el-row class="table-btns" type="flex" justify="space-between">
        <div style="display: flex; align-items: center; gap: 4px;">
          <el-button
            type="text"
            icon="el-icon-edit"
            @click="openAddRecord()"
            size="mini"
            class="text-btn"
            >{{ $t("add record") }}</el-button
          >

          <el-button
            type="text"
            icon="el-icon-upload"
            size="mini"
            class="text-btn"
            @click="openExcelUpload"
            >{{ $t("use excel to create") }}</el-button
          >

          <a
            :href="wantedTemplatUrl"
            download="wanted_template.xlsx"
            style="text-decoration: none;"
          >
            <el-button
              type="text"
              size="mini"
              icon="el-icon-download"
              class="text-btn"
            >
              {{ $t("download template") }}
            </el-button>
          </a>
        </div>
      </el-row>
    </div>

    <div
      v-if="loginInfo.orgType == 1"
      class="data-table"
      :style="{
        top: queryTableHeight,
        height: `calc(100% - ${queryTableHeight} - 70px)`,
      }"
      :fit="false"
    >
      <el-table
        :data="tableData.records"
        v-loading="isloading"
        element-loading-background="rgba(255,255,255,0.4)"
        size="mini"
        height="100%"
      >
        <el-table-column
          type="selection"
          fixed="left"
          width="50"
        ></el-table-column>
        <el-table-column
          prop="id"
          :label="$t('ID')"
          width="100"
        ></el-table-column>
        <el-table-column
          prop="nomPrenom"
          :label="$t('name')"
          width="160"
        ></el-table-column>
        <el-table-column :label="$t('country')">
          <template slot-scope="scope">
            {{ getCountryName(scope.row.country) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="position"
          :label="$t('position')"
          width="160"
        ></el-table-column>

        <el-table-column :label="$t('dateOfBirth')" width="160">
          <template slot-scope="scope">
            {{ formatDate(scope.row.dateNaissance) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="lieuNaissance"
          :label="$t('placeOfBirth')"
          width="160"
        >
        </el-table-column>

        <el-table-column
          prop="idPiece"
          :label="$t('idNumber')"
          width="160"
        ></el-table-column>
        <el-table-column
          prop="typePiece"
          :label="$t('idCardType')"
          width="160"
        ></el-table-column>
        <el-table-column prop="status" :label="$t('status')" width="160">
          <template slot-scope="scope">
            <span v-if="scope.row.status == 0" class="text-success">{{
              $t("normal")
            }}</span>
            <span v-else-if="scope.row.status == 1" class="text-danger">{{
              $t("wanted")
            }}</span>
            <span v-else-if="scope.row.status == 2" class="text-primary">{{
              $t("frozen_account")
            }}</span>
            <span v-else class="text-warning">{{ $t("PPE") }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="dateAjout"
          :label="$t('creationTime')"
          width="160"
        ></el-table-column>
        <el-table-column
          prop="createId"
          :label="$t('user')"
          width="160"
        ></el-table-column>

        <el-table-column fixed="right" :label="$t('extend')" width="100">
          <template slot-scope="scope">
            <el-button
              size="mini"
              @click="openCheckApply(scope.row)"
              type="primary"
              >{{ $t("edit") }}</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-pagination
      background
      layout="total,sizes,prev,pager,next,jumper"
      :total="tableData.total || 0"
      :page-sizes="[20, 30, 50, 100, 200]"
      :page-size="pageSize"
      :current-page="currentPage"
      @size-change="setPageSzie"
      @current-change="setCurrentChange"
      @prev-click="setCurrentChange"
      @next-click="setCurrentChange"
    />

    <el-drawer
      :title="$t('add record')"
      :visible.sync="addRecordDrawer"
      :direction="'rtl'"
      :modal="false"
      :destroy-on-close="true"
      :wrapperClosable="false"
      :size="700"
    >
      <el-form
        :model="addForm"
        :rules="rules"
        ref="addForm"
        label-position="top"
      >
        <el-form-item :label="$t('name')" prop="nomPrenom">
          <el-input size="medium" v-model="addForm.nomPrenom" />
        </el-form-item>
        <el-form-item :label="$t('position')" prop="position">
          <el-input size="medium" v-model="addForm.position" />
        </el-form-item>

        <el-form-item :label="$t('country')" prop="country">
          <el-select
            size="medium"
            v-model="addForm.country"
            filterable
            :placeholder="$t('Please select country')"
            style="width: 100%;"
          >
            <el-option
              v-for="item in countryList"
              :key="item.id"
              :label="$i18n.locale === 'fr' ? item.nameFr : item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('dateOfBirth')" prop="dateNaissance">
          <el-date-picker
            v-model="addForm.dateNaissance"
            type="datetime"
            :time-arrow-control="true"
            :format="'yyyy-MM-dd'"
            :placeholder="$t('selectDateAndTime')"
            style="width:100%"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item :label="$t('placeOfBirth')" prop="lieuNaissance">
          <el-input size="medium" v-model="addForm.lieuNaissance" />
        </el-form-item>

        <el-form-item :label="$t('idNumber')" prop="idPiece">
          <el-input size="medium" v-model="addForm.idPiece" />
        </el-form-item>
        <el-form-item :label="$t('idCardType')" prop="typePiece">
          <el-input size="medium" v-model="addForm.typePiece" />
        </el-form-item>

        <el-form-item class="bottom-button">
          <el-button
            size="medium"
            type="primary"
            @click="addSubmit('addForm')"
            :loading="isloading"
            >{{ $t("submit") }}</el-button
          >
        </el-form-item>
      </el-form>
    </el-drawer>

    <el-drawer
      :title="$t('Validation')"
      :visible.sync="isCheckApply"
      :direction="'rtl'"
      :modal="false"
      :destroy-on-close="true"
      :wrapperClosable="false"
      :size="700"
    >
      <el-form :model="checkForm" ref="checkForm" label-position="top">
        <el-form-item :label="$t('ID')" prop="id">
          <el-input size="medium" v-model="checkForm.id" disabled="" />
        </el-form-item>
        <el-form-item :label="$t('name')" prop="nomPrenom">
          <el-input size="medium" v-model="addForm.nomPrenom" disabled="" />
        </el-form-item>
        <el-form-item :label="$t('position')" prop="position">
          <el-input size="medium" v-model="addForm.position" disabled="" />
        </el-form-item>
        <el-form-item :label="$t('country')" prop="country">
          <template>
            {{ getCountryName(addForm.country) }}
          </template>
        </el-form-item>

        <el-form-item :label="$t('dateOfBirth')" prop="dateNaissance">
          <el-date-picker
            v-model="addForm.dateNaissance"
            disabled=""
            style="width:100%"
          >
          </el-date-picker>
        </el-form-item>

        <el-form-item :label="$t('placeOfBirth')" prop="lieuNaissance">
          <el-input size="medium" v-model="addForm.lieuNaissance" disabled="" />
        </el-form-item>

        <el-form-item :label="$t('idNumber')" prop="idPiece">
          <el-input size="medium" v-model="addForm.idPiece" disabled="" />
        </el-form-item>
        <el-form-item :label="$t('idCardType')" prop="typePiece">
          <el-input size="medium" v-model="addForm.typePiece" disabled="" />
        </el-form-item>
        <el-divider content-position="left">{{ $t("checkInfo") }}</el-divider>
        <el-form-item :label="$t('checkStatus')" prop="status">
          <el-radio v-model="checkForm.status" :label="0" border>{{
            $t("normal")
          }}</el-radio>
          <el-radio v-model="checkForm.status" :label="1" border>{{
            $t("wanted")
          }}</el-radio>
          <el-radio v-model="checkForm.status" :label="2" border>{{
            $t("frozen_account")
          }}</el-radio>
          <el-radio v-model="checkForm.status" :label="3" border>{{
            $t("PPE")
          }}</el-radio>
        </el-form-item>

        <el-form-item class="bottom-button">
          <el-button
            size="medium"
            type="primary"
            @click="updateSubmit('checkForm')"
            :loading="isloading"
          >
            {{ $t("submit") }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-drawer>

    <!-- Dialog Upload Excel -->
    <el-dialog
      :title="$t('use excel to create')"
      :visible.sync="excelUploadVisible"
      width="600px"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
    >
      <el-upload
        ref="uploadExcel"
        action="/back/wanted/batch-import"
        accept=".xls,.xlsx"
        :auto-upload="false"
        :on-success="excelUploadSuccess"
        :on-change="onFileChange"
        :on-remove="onFileRemove"
        :file-list="fileList"
        :multiple="false"
        :limit="1"
        name="file"
        list-type="text"
      >
        <el-button
          v-if="!selectedFile"
          slot="trigger"
          size="small"
          type="primary"
          >{{ $t("select") }} EXCEL</el-button
        >

        <div slot="tip" class="el-upload__tip">
          {{ $t("onlyUploadEXCELFileAndInXLSXOrXLSFormat") }};
          {{ $t("No template") }}?
          <a :href="wantedTemplatUrl" download="wanted_template.xlsx">
            {{ $t("click to download") }}
          </a>
        </div>
      </el-upload>

      <!-- Preview table -->
      <el-table
        v-if="previewData.length > 0"
        :data="previewData.slice(0, 5)"
        border
        size="mini"
        style="margin-top: 12px; width: 100%"
      >
        <el-table-column prop="nomPrenom" :label="$t('name')" />
        <el-table-column prop="country" :label="$t('country')" />
        <el-table-column prop="dateNaissance" :label="$t('dateOfBirth')" />
        <el-table-column prop="idPiece" :label="$t('idNumber')" />
      </el-table>
      <div
        v-if="previewData.length > 5"
        style="font-size:12px; color:#999; margin-top:4px;"
      >
        ... {{ previewData.length - 5 }} {{ $t("more rows") }}
      </div>

      <!-- Progress -->
      <el-progress
        v-if="uploadProgress > 0"
        :percentage="uploadProgress"
        :status="uploadProgress === 100 ? 'success' : null"
        style="margin-top: 12px"
      />

      <el-alert
        v-if="uploadResult"
        :title="uploadResult.message"
        :type="uploadResult.type"
        show-icon
        :closable="false"
        style="margin-top: 12px"
      />

      <span slot="footer">
        <el-button @click="closeUploadDialog">{{ $t("cancel") }}</el-button>
        <el-button
          type="primary"
          :loading="isUploading"
          :disabled="!selectedFile"
          @click="submitExcelUpload"
          >{{ $t("upload") }} EXCEL</el-button
        >
      </span>
    </el-dialog>
    <!-- end dialog-->
  </el-card>
</template>
<script>
import wantedPerson from "@/api/wantedPerson";
import manba from "@/assets/uilt/manbaDate";
import { countries } from "@/assets/data/countries";
import qs from "qs";

export default {
  name: "listOfWantedPersons",
  data() {
    return {
      queryForm: {},
      addForm: {},
      checkForm: {},
      excelUploadVisible: false,
      uploadProgress: 0,
      uploadResult: null,
      isUploading: false,
      selectedFile: null,
      fileList: [],
      previewData: [],
      //selectedFile: null,
      countryList: countries,
      rules: {
        name: [
          {
            required: true,
            message: this.$t("requiredFields"),
            trigger: "blur",
          },
        ],
        nomPrenom: [
          {
            required: true,
            message: this.$t("requiredFields"),
            trigger: "blur",
          },
        ],
      },
      //table高度，动态设置
      queryTableHeight: "",
      //table显示内容
      tableData: {},
      isCheckApply: false,

      pageSize: 20,
      currentPage: 1,

      //显示加载中
      isloading: false,
      //新增\编辑角色
      addRecordDrawer: false,
    };
  },
  computed: {
    loginInfo() {
      return this.$store.state.globalParameter.loginInfo;
    },
    wantedTemplatUrl() {
      return "/back/wanted/template";
    },
    excelUploadUrl() {
      return "/back/wanted/batch-import";
    },
    downloadUrl() {
      let _this = this;
      let param = JSON.parse(JSON.stringify(_this.queryForm));

      delete param.size;
      delete param.current;

      return (
        wantedPerson.wantedPersonDetailedExport + "?" + qs.stringify(param)
      );
    },
    displayCountries() {
      return countries
        .map((c) => ({
          id: c.id,
          name: this.$i18n.locale === "fr" ? c.nameFr : c.name, // ✅ Use this.$i18n.locale
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  watch: {},
  methods: {
    downloadEXCEL() {
      let download = document.createElement("a");
      download.href = this.downloadUrl;
      download.download = "";
      download.click();
    },
    getCountryName(countryCode) {
      if (!countryCode) return "-";

      const country = this.countryList.find((c) => c.id === countryCode);
      if (!country) return countryCode;

      // Return name based on current language
      return this.$i18n.locale === "fr" ? country.nameFr : country.name;
    },
    // Format date to YYYY-MM-DD
    formatDate(dateString) {
      if (!dateString) return "-";

      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    },
    /**getWantedPersonsPage */
    getList() {
      let _this = this;
      wantedPerson.getWantedPage({
        param: {
          current: _this.currentPage,
          size: _this.pageSize,
          ..._this.queryForm,
        },
        success: (res) => {
          if (res.code == 200) _this.isloading = false;
          _this.tableData = res.data || {};
        },
      });
    },
    setPageSzie(val) {
      this.pageSize = val;
      this.getList();
    },
    setCurrentChange(val) {
      this.currentPage = val;
      this.getList();
    },
    openAddRecord() {
      this.addRecordDrawer = true;
    },
    openCheckApply(row) {
      this.isCheckApply = true;
      this.addForm = row;
      this.checkForm = {
        id: row.id,
        status: row.status,
      };
    },
    addSubmit(formName) {
      let _this = this;

      _this.$refs[formName].validate((valid) => {
        if (valid) {
          _this.isloading = true;
          _this[formName].dateNaissance = manba(
            _this[formName].dateNaissance,
          ).format("ymdhms");

          let aubmitForm = JSON.parse(JSON.stringify(_this.addForm));

          wantedPerson.addWantedApply({
            param: aubmitForm,
            success: (res) => {
              if (res.code == 200) {
                _this.$message({
                  type: "success",
                  message: _this.$t("operateSuccessfully"),
                });
                _this.getList();
                _this.addRecordDrawer = false;
              } else {
                _this.$message({ type: "warning", message: _this.$t(res.msg) });
              }
              _this.isloading = false;
            },
          });
        }
      });
    },
    openExcelUpload() {
      this.excelUploadVisible = true;
      this.uploadProgress = 0;
      this.uploadResult = null;
      this.selectedFile = null;
      this.fileList = [];
      this.previewData = [];
    },

    closeUploadDialog() {
      this.excelUploadVisible = false;
      this.$refs.uploadExcel && this.$refs.uploadExcel.clearFiles();
    },

    onFileChange(file) {
      this.selectedFile = file;
      this.uploadResult = null;
      this.uploadProgress = 0;
      this.readExcelPreview(file.raw);
    },

    onFileRemove() {
      this.selectedFile = null;
      this.previewData = [];
    },

    readExcelPreview(file) {
      if (!file) return;
      const fileReader = new FileReader();
      fileReader.onload = (ev) => {
        try {
          const XLSX = require("xlsx");
          const data = ev.target.result;
          const workbook = XLSX.read(data, { type: "binary" });
          const wsname = workbook.SheetNames[0];
          const ws = XLSX.utils.sheet_to_json(workbook.Sheets[wsname]);
          this.previewData = ws;
        } catch (e) {
          console.error("Preview error:", e);
        }
      };
      fileReader.readAsBinaryString(file);
    },

    submitExcelUpload() {
      if (!this.selectedFile) {
        this.$message.warning(this.$t("please select a file"));
        return;
      }
      this.isUploading = true;
      this.uploadProgress = 50;
      this.$refs.uploadExcel.submit();
    },

    excelUploadSuccess(res) {
      this.isUploading = false;
      this.uploadProgress = 100;
      if (res.code == 200) {
        this.uploadResult = {
          type: "success",
          message: this.$t("operateSuccessfully"),
        };
        this.getList();
        setTimeout(() => {
          this.closeUploadDialog();
        }, 1500);
      } else {
        this.uploadResult = {
          type: "warning",
          message: this.$t(res.msg || "operationFailure"),
        };
        this.uploadProgress = 0;
      }
    },

    excelUploadError(err) {
      this.isUploading = false;
      this.uploadProgress = 0;
      this.uploadResult = {
        type: "error",
        message: "Upload failed: " + err.message,
      };
    },

    updateSubmit(formName) {
      let _this = this;
      _this.isloading = true;
      _this.$refs[formName].validate((valid) => {
        if (valid) {
          let aubmitForm = JSON.parse(JSON.stringify(_this.checkForm));
          wantedPerson.disposeWantedApply({
            param: aubmitForm,
            success: (res) => {
              if (res.code == 200) {
                _this.$message({
                  type: "success",
                  message: _this.$t("operateSuccessfully"),
                });
                _this.getList();
                _this.isCheckApply = false;
              } else {
                _this.$message({ type: "warning", message: _this.$t(res.msg) });
              }
              _this.isloading = false;
            },
          });
        }
      });
    },
  },
  mounted() {
    let _this = this;
    this.$nextTick(() => {
      let queryTableHeight = _this.$refs.pageHead.offsetHeight + 15;
      _this.queryTableHeight = queryTableHeight + "px";
      _this.getList();
    });
  },
};
</script>

<style lang="less" scoped>
.tree-card {
  height: 300px;
}
</style>
